import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "@/src/components/ui/input";
import { TrashIcon, PlusIcon } from "lucide-react";
import { useBookingEditEmailMutation } from "@/src/lib/bookingEditEmailMutation";
import { useToast } from "@/src/components/ui/use-toast";
import { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../../../components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../../../components/ui/form";
import { Button } from "@/src/components/ui/button";

const userDataSchema = z.object({
  email: z.string(),
  additionalEmails: z.array(z.string()),
});

type CustomerEmailForm = z.infer<typeof userDataSchema>;

const EditCustomerEmailModal = ({
  open,
  onOpenChange,
  bookingId,
  offerId,
  defaultValues,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValues: {
    email: string;
    emails?: string[];
  };
  bookingId?: string;
  offerId?: string;
}) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const editEmailMutation = useBookingEditEmailMutation(
    onOpenChange,
    setShowConfirmModal,
    bookingId ? bookingId : undefined,
    offerId ? offerId : undefined,
  );
  const form = useForm<CustomerEmailForm>({
    resolver: zodResolver(userDataSchema),
    defaultValues: defaultValues,
  });
  const { control } = form;
  const { toast } = useToast();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "additionalEmails" as never,
  });
  const onSubmit = useCallback(
    (force = false) => {
      return form.handleSubmit(
        (data) => {
          const toastId = toast({
            description: "Updating customer email...",
            variant: "default",
            duration: Infinity,
          }).id;

          editEmailMutation.mutate({ ...data, toastId, force });
        },
        () => {
          console.error("FORM ERROR", form.formState.errors, form.getValues());
        },
      )();
    },
    [form, editEmailMutation],
  );

  useEffect(() => {
    if (open) {
      form.reset({
        email: defaultValues.email,
        additionalEmails: defaultValues.emails ?? [],
      });
    } else {
      setShowConfirmModal(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-[720px] max-h-[calc(100%-80px)] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="text-2xl flex flex-col">Edit customer email</DialogTitle>
        </DialogHeader>
        {!showConfirmModal && (
          <Form {...form}>
            <div className="gap-4">
              <FormField
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-slate-600">E-mail</FormLabel>
                    <FormControl>
                      <Input type="text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="mt-4 space-y-4">
                {fields.length > 0 && (
                  <>
                    <div className="text-sm bg-blue-100 w-full h-[1px] mt-6 mb-4" />
                    <FormLabel className="text-slate-600">Additonal e-mails</FormLabel>
                    {fields.map((field, index) => (
                      <FormField
                        key={field.id}
                        control={control}
                        name={`additionalEmails.${index}`}
                        render={({ field }) => (
                          <FormItem className="flex items-center gap-2">
                            <FormControl>
                              <Input placeholder={`additional email`} type="text" {...field} />
                            </FormControl>
                            <Button type="button" variant="outline" className="!mt-0" onClick={() => remove(index)}>
                              <TrashIcon size={16} />
                            </Button>
                          </FormItem>
                        )}
                      />
                    ))}
                  </>
                )}

                <Button
                  onClick={() => {
                    append("");
                  }}
                  className="col-start-1 text-sm"
                  type="button"
                  variant="secondary"
                >
                  Add additonal email
                  <PlusIcon className="ml-2 w-4 h-4 stroke-slate-900" />
                </Button>
              </div>
            </div>
          </Form>
        )}
        {showConfirmModal && (
          <div className="flex flex-col gap-6">
            <p className="text-slate-700">
              This email ({form.getValues("email")}) is currently linked to another customer. Would you like to{" "}
              <strong>assign it to this {bookingId ? "booking" : "offer"}</strong> instead?
            </p>
          </div>
        )}
        <div className="-mx-6 mt-2 mb-2">
          <hr className="border-t border-gray-200 w-full" />
        </div>
        <DialogFooter>
          {!showConfirmModal && (
            <Button onClick={() => onSubmit()} type="submit">
              Save
            </Button>
          )}
          {showConfirmModal && (
            <>
              <Button
                className="mr-auto"
                variant="secondary"
                onClick={() => {
                  setShowConfirmModal(false);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  onSubmit(true);
                }}
                type="submit"
              >
                Confirm
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditCustomerEmailModal;
